import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { AddToListButton } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import DependencyForm from "~/components/tasks/Dependencies/DependencyForm";
import { TemplateTaskDependenciesList } from "~/pages/ProjectTemplates/Tasks/Dependencies/TemplateTaskDependenciesList";
import { invalidateTemplateDependenciesCount } from "~/repository";
import { ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS } from "~/store/action_types";
import {
  createProjectTemplateTasksDependency,
  fetchProjectTemplateTaskDependencies,
  fetchProjectTemplateTaskDependenciesSuggestions,
} from "~/store/actions/actions.projectTemplateTaskDependencies";
import {
  makeGetAllChildrenByProjectTemplateTaskIdSelector,
  makeGetAllGroupedSuggestionsByTaskIdSelector,
  makeGetAllParentsByProjectTemplateTaskIdSelector,
} from "~/store/selectors/selector.projectTemplateTaskDependencies";

export const TemplateTaskDependencies = ({ taskId, taskListId, templateId, className }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const [showForm, setShowForm] = useState(false);

  const openForm = useCallback(() => {
    setShowForm(true);
  }, []);

  const closeForm = useCallback(() => {
    setShowForm(false);
  }, []);

  useEffect(() => {
    dispatch(fetchProjectTemplateTaskDependencies(taskId));
    dispatch(fetchProjectTemplateTaskDependenciesSuggestions(taskId));
  }, [dispatch, taskId]);

  const getProjectTemplateParents = useMemo(makeGetAllParentsByProjectTemplateTaskIdSelector, []);
  const parents = useSelector((state) => getProjectTemplateParents(state, taskId));
  const getProjectTemplateChildren = useMemo(makeGetAllChildrenByProjectTemplateTaskIdSelector, []);
  const children = useSelector((state) => getProjectTemplateChildren(state, taskId));
  const getAllSuggestions = useMemo(makeGetAllGroupedSuggestionsByTaskIdSelector, []);
  const suggestions = useSelector((state) => getAllSuggestions(state, taskId));

  const handleSubmit = useCallback(
    (data) => {
      dispatch(createProjectTemplateTasksDependency(taskId, data)).then((res) => {
        if (res.type === ADD_PROJECT_TEMPLATE_TASK_DEPENDENCIES_SUCCESS) {
          closeForm();
          invalidateTemplateDependenciesCount(templateId);
        }
      });
    },
    [dispatch, taskId, closeForm, templateId]
  );

  return (
    <div
      className={classNames(
        "task_dependencies tw-mb-4 tw-border-b tw-border-solid tw-border-0 tw-border-border-primary tw-pb-4 lg:tw-border-0",
        className
      )}
    >
      {parents.length ? (
        <TemplateTaskDependenciesList
          tasks={parents}
          parentId={taskId}
          isParent
          className="tw-mb-[6px]"
          title={t("Predecessors")}
        />
      ) : null}
      {children.length ? (
        <TemplateTaskDependenciesList
          tasks={children}
          parentId={taskId}
          title={t("Successors")}
        />
      ) : null}
      {suggestions.length > 0 &&
        (showForm ? (
          <DependencyForm
            handleCancel={closeForm}
            className="task_dependencies_form"
            tasks={suggestions}
            taskListId={taskListId}
            taskId={taskId}
            isAddNewEnabled={false}
            onSubmit={handleSubmit}
          />
        ) : (
          <AddToListButton
            className="-tw-ml-1"
            onClick={openForm}
            text={t("Add a Dependency")}
          />
        ))}
    </div>
  );
};

TemplateTaskDependencies.propTypes = {
  taskId: PropTypes.number.isRequired,
  taskListId: PropTypes.number.isRequired,
  templateId: PropTypes.number.isRequired,
  className: PropTypes.string,
};
